import { useCallback, useState, type ReactNode } from 'react';
import { FilterChip, type FilterChipOption } from '../../molecules/FilterChip/FilterChip';
import styles from './FiltersBar.module.css';

/**
 * Figma: `Filters Bar` (781:15281) — a component set with `Type = Default |
 * Competitors`, wrapping what used to be the standalone component now called
 * `Type=Default` (200:3439).
 *
 * The annotation still describes the bar itself: "A persistent filter bar for
 * analytics and dashboard pages. Allows users to narrow data across multiple
 * dimensions simultaneously (e.g. by brand, channel, topic). Each chip opens a
 * multi-select dropdown."
 *
 * The six chips on the canvas — Brands, Channels, Intents, Topics,
 * Conversations, Sentiments — are content, not structure, so `filters` drives
 * them the way `Sidebar` takes `sections`. Each also swaps its own icon into the
 * chip's leading slot, which `filters` carries for the same reason.
 *
 * **What the variant actually changes is one chip.** Both variants are identical
 * box for box — same `space/8`/`space/24` padding, same `space/12` gap, same
 * `surface/background` fill, bottom stroke and blur. The only difference is that
 * `Brands` is `visible: false` in `Type=Default` and visible in
 * `Type=Competitors`: the brand filter belongs to the competitors screen, where
 * choosing which brands to compare is the point, and is noise everywhere else.
 *
 * That rule is expressed as data rather than as a hardcoded id. `type` picks the
 * variant and each filter may declare the types it belongs to, so the bar never
 * has to know that the chip called "brands" is special — see `types` below. A
 * caller who wants different chips per screen keeps doing what it already does
 * and passes a different `filters` array.
 */
export type FiltersBarType = 'default' | 'competitors';
export type FiltersBarFilter = {
  id: string;
  /** The chip's label, e.g. "Brands". */
  label: string;
  /**
   * The chip's leading icon. Figma swaps a different symbol into every chip on
   * the canvas — `brand`, `channel`, `intent`, `topics`, `conversations`,
   * `sentiments` — so this is content like the label, not structure. Omit it
   * and the chip falls back to Figma's `placeholder`.
   */
  icon?: ReactNode;
  options: FilterChipOption[];
  /**
   * The bar types this filter appears in. Omitted, it appears in every type,
   * which is what five of the six chips do.
   *
   * Figma expresses this as a layer's `visible` flag per variant; here it is the
   * filter's own declaration, so adding a screen-specific chip is a fixture edit
   * rather than a change to `FiltersBar`.
   */
  types?: FiltersBarType[];
  /** Controlled selection. Omit to let the chip own it. */
  selected?: string[];
  defaultSelected?: string[];
  onSelectionChange?: (values: string[]) => void;
  searchPlaceholder?: string;
};

export type FiltersBarProps = {
  filters: FiltersBarFilter[];
  /**
   * Figma `Type`. `competitors` is the competitors screen, which is the only one
   * that draws the `Brands` chip.
   */
  type?: FiltersBarType;
  /**
   * Controlled id of the chip whose panel is open, or `null` for none. Omit to
   * let the bar own it.
   */
  openFilterId?: string | null;
  defaultOpenFilterId?: string | null;
  onOpenFilterChange?: (id: string | null) => void;
  /**
   * Accessible name for the group. Not in Figma — a row of unlabelled controls
   * gives a screen reader nothing to say about what they filter.
   */
  label?: string;
  className?: string;
};

export function FiltersBar({
  filters,
  type = 'default',
  openFilterId,
  defaultOpenFilterId = null,
  onOpenFilterChange,
  label = 'Filters',
  className,
}: FiltersBarProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState<string | null>(defaultOpenFilterId);
  const currentOpen = openFilterId !== undefined ? openFilterId : uncontrolledOpen;

  /*
   * Not in Figma — Figma shows six independent instances and has no way to
   * express that opening one closes the others. Two filter panels overlapping
   * each other is not a state the design contemplates, so the bar arbitrates.
   * A caller that wants the chips independent can drive `openFilterId` itself.
   */
  const setOpen = useCallback(
    (id: string, next: boolean) => {
      const value = next ? id : null;
      if (openFilterId === undefined) setUncontrolledOpen(value);
      onOpenFilterChange?.(value);
    },
    [openFilterId, onOpenFilterChange],
  );

  /*
   * Figma hides `Brands` on `Type=Default` rather than removing it, because a
   * variant has to keep every layer. Here an out-of-type filter is simply not
   * rendered — there is no hidden node to leave in the DOM, and a chip a screen
   * does not have should not be reachable by a screen reader or the keyboard.
   */
  const visible = filters.filter((f) => !f.types || f.types.includes(type));

  return (
    <div className={[styles.bar, className].filter(Boolean).join(' ')}>
      {/*
       * `role="group"` rather than `toolbar`: a toolbar promises roving-focus
       * arrow-key navigation, which this does not implement. Not in Figma.
       */}
      <div className={styles.wrapper} role="group" aria-label={label}>
        {visible.map((filter) => (
          <FilterChip
            key={filter.id}
            label={filter.label}
            icon={filter.icon}
            options={filter.options}
            selected={filter.selected}
            defaultSelected={filter.defaultSelected}
            onSelectionChange={filter.onSelectionChange}
            searchPlaceholder={filter.searchPlaceholder}
            open={currentOpen === filter.id}
            onOpenChange={(next) => setOpen(filter.id, next)}
          />
        ))}
      </div>
    </div>
  );
}
